// Interactive Onboarding Tour Component - TypeTown
// Guides new users through the game mechanics with "Give it a go!" prompts

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Keyboard, Users, Home, Star, Trophy } from 'lucide-react';

const TOUR_STEPS = [
  {
    id: 'welcome',
    title: "Welcome to TypeTown! 🏘️",
    content: "TypeTown is a fun typing adventure where you explore, meet characters, and build your own town - all while improving your typing skills!",
    emoji: "🎉",
    position: 'center',
    highlight: null,
    action: null,
  },
  {
    id: 'movement',
    title: "Moving Around 🚶",
    content: "Use the Arrow Keys or W, A, S, D to move your character around the island. Try walking around and exploring!",
    emoji: "⌨️",
    position: 'bottom-left',
    highlight: 'character',
    action: {
      type: 'movement',
      prompt: "Give it a go! Walk around using the arrow keys.",
      success: "Great job! You're a natural explorer! 🌟"
    }
  },
  {
    id: 'npcs',
    title: "Meet the Locals 👋",
    content: "See those friendly characters with names above their heads? Walk up to them and click to start a conversation!",
    emoji: "💬",
    position: 'top-right',
    highlight: 'npcs',
    action: {
      type: 'npc-click',
      prompt: "Give it a go! Click on a character to chat with them.",
      success: "Awesome! You've made a new friend! 🎊"
    }
  },
  {
    id: 'typing-intro',
    title: "Typing to Talk 📝",
    content: "To ask questions, you'll need to type them out! This helps you practice typing while having fun conversations.",
    emoji: "⌨️",
    position: 'center',
    highlight: null,
    action: null,
  },
  {
    id: 'build-button',
    title: "Build Your Town 🏗️",
    content: "Click the BUILD button to see what you can place in your town. Complete typing levels to unlock more buildings!",
    emoji: "🏠",
    position: 'bottom-right',
    highlight: 'build-button',
    action: {
      type: 'build-click',
      prompt: "Give it a go! Click the BUILD button to explore.",
      success: "You found the Build Menu! So many things to create! 🎨"
    }
  },
  {
    id: 'typing-button',
    title: "Start Typing Challenges ⭐",
    content: "Click the TYPING button to practice your typing skills. Type phrases correctly to earn credits and level up!",
    emoji: "🎯",
    position: 'bottom-center',
    highlight: 'typing-button',
    action: {
      type: 'typing-click',
      prompt: "Give it a go! Click TYPING to try a challenge.",
      success: "Ready to type? Let's see those fast fingers! ⚡"
    }
  },
  {
    id: 'credits',
    title: "Earn Credits 💰",
    content: "You earn credits by completing typing challenges and chatting with NPCs. Use credits to buy buildings and customize your character!",
    emoji: "✨",
    position: 'top-right',
    highlight: 'credits',
    action: null,
  },
  {
    id: 'character',
    title: "Customize Your Look 🎨",
    content: "Click the CHARACTER button to change your avatar's appearance - hair, clothes, accessories and more!",
    emoji: "👤",
    position: 'bottom-left',
    highlight: 'character-button',
    action: null,
  },
  {
    id: 'zones',
    title: "Explore New Zones 🗺️",
    content: "TypeTown has 5 unique zones to discover! Level up to unlock the Snowy North, Eastern Hills, Southern Beach, and Western Mountains.",
    emoji: "🌍",
    position: 'center',
    highlight: 'minimap',
    action: null,
  },
  {
    id: 'complete',
    title: "You're Ready! 🎉",
    content: "That's everything you need to know! Start exploring, make friends, practice typing, and build your dream town. Have fun!",
    emoji: "🚀",
    position: 'center',
    highlight: null,
    action: null,
  }
];

const OnboardingTour = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  
  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  
  // Listen for keyboard movement
  useEffect(() => {
    if (step.action?.type === 'movement' && !actionCompleted) {
      const handleKeyDown = (e) => {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd', 'W', 'A', 'S', 'D'].includes(e.key)) {
          setHasMoved(true);
          setTimeout(() => {
            setActionCompleted(true);
            setShowSuccessMessage(true);
          }, 1500);
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [step, actionCompleted]);
  
  // Listen for clicks on specific elements
  useEffect(() => {
    if (!step.action || actionCompleted) return;
    
    const handleClick = (e) => {
      const target = e.target;
      
      // Check for NPC click
      if (step.action.type === 'npc-click') {
        if (target.closest('[data-testid^="npc-"]')) {
          setActionCompleted(true);
          setShowSuccessMessage(true);
        }
      }
      
      // Check for BUILD button click
      if (step.action.type === 'build-click') {
        if (target.closest('[data-testid="build-button"]') || target.textContent?.includes('BUILD')) {
          setActionCompleted(true);
          setShowSuccessMessage(true);
        }
      }
      
      // Check for TYPING button click
      if (step.action.type === 'typing-click') {
        if (target.closest('[data-testid="typing-button"]') || target.textContent?.includes('TYPE')) {
          setActionCompleted(true);
          setShowSuccessMessage(true);
        }
      }
    };
    
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [step, actionCompleted]);
  
  const handleNext = useCallback(() => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
      setActionCompleted(false);
      setShowSuccessMessage(false);
      setHasMoved(false);
    }
  }, [isLastStep, onComplete]);
  
  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
      setActionCompleted(false);
      setShowSuccessMessage(false);
      setHasMoved(false);
    }
  };
  
  const handleSkip = () => {
    onSkip();
  };
  
  // Position styles for the tooltip
  const getPositionStyle = () => {
    switch (step.position) {
      case 'center':
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
      case 'top-left':
        return 'top-4 sm:top-24 left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-8';
      case 'top-right':
        return 'top-4 sm:top-24 left-1/2 -translate-x-1/2 sm:translate-x-0 sm:right-8 sm:left-auto';
      case 'bottom-left':
        return 'bottom-4 sm:bottom-32 left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-8';
      case 'bottom-right':
        return 'bottom-4 sm:bottom-32 left-1/2 -translate-x-1/2 sm:translate-x-0 sm:right-8 sm:left-auto';
      case 'bottom-center':
        return 'bottom-4 sm:bottom-32 left-1/2 -translate-x-1/2';
      default:
        return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
    }
  };
  
  // Get highlight element style
  const getHighlightStyle = () => {
    if (!step.highlight) return null;
    
    switch (step.highlight) {
      case 'build-button':
        return { bottom: '20px', right: '20px', width: '120px', height: '50px' };
      case 'typing-button':
        return { bottom: '20px', right: '150px', width: '120px', height: '50px' };
      case 'character-button':
        return { bottom: '20px', left: '20px', width: '140px', height: '50px' };
      case 'credits':
        return { top: '20px', right: '20px', width: '150px', height: '50px' };
      case 'minimap':
        return { top: '20px', left: '20px', width: '200px', height: '200px' };
      case 'npcs':
        return { top: '30%', left: '30%', width: '40%', height: '40%', borderRadius: '20px' };
      case 'character':
        return { top: '40%', left: '45%', width: '100px', height: '150px', borderRadius: '10px' };
      default:
        return null;
    }
  };
  
  const highlightStyle = getHighlightStyle();
  
  const hasActiveAction = step.action && !actionCompleted;

  return (
    <div className={`fixed inset-0 z-[1000] ${hasActiveAction ? 'pointer-events-none' : ''}`} data-testid="onboarding-tour">
      {/* Semi-transparent overlay */}
      <div className={`absolute inset-0 ${hasActiveAction ? 'bg-black/20' : 'bg-black/40'} pointer-events-none`} />
      
      {/* Highlight cutout */}
      {highlightStyle && (
        <div 
          className="absolute border-4 border-sunny-yellow rounded-xl animate-pulse shadow-lg shadow-sunny-yellow/50 pointer-events-none"
          style={highlightStyle}
        />
      )}
      
      {/* Tour card */}
      <div className={`absolute ${getPositionStyle()} w-[92%] max-w-md z-[1001] pointer-events-auto`}>
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border-4 border-mint-green overflow-hidden max-h-[85vh] flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-mint-green via-sunny-yellow to-coral-orange p-3 sm:p-4 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <span className="text-2xl sm:text-4xl shrink-0">{step.emoji}</span>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-display font-bold text-white drop-shadow-md truncate">{step.title}</h2>
                  <p className="text-white/80 text-xs sm:text-sm">Step {currentStep + 1} of {TOUR_STEPS.length}</p>
                </div>
              </div>
              <button 
                onClick={handleSkip}
                className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white transition-all shrink-0"
                aria-label="Skip tour"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="h-1.5 sm:h-2 bg-soft-gray shrink-0">
            <div 
              className="h-full bg-gradient-to-r from-mint-green to-sunny-yellow transition-all duration-500"
              style={{ width: `${((currentStep + 1) / TOUR_STEPS.length) * 100}%` }}
            />
          </div>
          
          {/* Content - scrollable */}
          <div className="p-4 sm:p-6 overflow-y-auto flex-1">
            <p className="text-text-dark text-sm sm:text-lg leading-relaxed mb-3 sm:mb-4">{step.content}</p>
            
            {/* Action prompt */}
            {step.action && !showSuccessMessage && (
              <div className="bg-gradient-to-r from-sunny-yellow/20 to-coral-orange/20 border-2 border-sunny-yellow rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4 animate-pulse">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-sunny-yellow rounded-full flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-sunny-yellow text-sm sm:text-lg">Try it now!</p>
                    <p className="text-text-dark text-xs sm:text-base">{step.action.prompt}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Success message */}
            {showSuccessMessage && (
              <div className="bg-gradient-to-r from-mint-green/20 to-sky-blue/20 border-2 border-mint-green rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4 animate-bounce">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-mint-green rounded-full flex items-center justify-center shrink-0">
                    <Star className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-mint-green text-sm sm:text-lg">Well done!</p>
                    <p className="text-text-dark text-xs sm:text-base">{step.action.success}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Keyboard hint for movement step */}
            {step.action?.type === 'movement' && !actionCompleted && (
              <div className="flex justify-center gap-2 mb-3 sm:mb-4">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-text-medium/10 rounded-lg flex items-center justify-center text-text-dark font-mono font-bold border-2 border-text-medium/20 text-xs sm:text-base">
                    W
                  </div>
                  <div className="flex gap-1">
                    {['A', 'S', 'D'].map(k => (
                      <div key={k} className="w-9 h-9 sm:w-12 sm:h-12 bg-text-medium/10 rounded-lg flex items-center justify-center text-text-dark font-mono font-bold border-2 border-text-medium/20 text-xs sm:text-base">
                        {k}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center text-text-medium px-2 sm:px-4 text-xs sm:text-base">or</div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-9 h-9 sm:w-12 sm:h-12 bg-text-medium/10 rounded-lg flex items-center justify-center text-text-dark font-bold border-2 border-text-medium/20 text-xs sm:text-base">
                    &uarr;
                  </div>
                  <div className="flex gap-1">
                    {['\u2190', '\u2193', '\u2192'].map((k, i) => (
                      <div key={i} className="w-9 h-9 sm:w-12 sm:h-12 bg-text-medium/10 rounded-lg flex items-center justify-center text-text-dark font-bold border-2 border-text-medium/20 text-xs sm:text-base">
                        {k}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Navigation buttons - always visible */}
          <div className="px-4 pb-3 sm:px-6 sm:pb-4 flex items-center justify-between shrink-0 border-t border-soft-gray/30 pt-3">
            <button
              onClick={handlePrevious}
              disabled={isFirstStep}
              className={`flex items-center gap-1 px-2 py-1.5 sm:px-4 sm:py-2 rounded-xl font-semibold transition-all text-xs sm:text-base ${
                isFirstStep 
                  ? 'text-text-medium/40 cursor-not-allowed' 
                  : 'text-lavender hover:bg-lavender/10'
              }`}
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              Back
            </button>
            
            <div className="flex gap-1 sm:gap-2">
              {TOUR_STEPS.map((_, index) => (
                <div 
                  key={index}
                  className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${
                    index === currentStep 
                      ? 'bg-mint-green w-4 sm:w-6' 
                      : index < currentStep 
                        ? 'bg-mint-green/50' 
                        : 'bg-soft-gray'
                  }`}
                />
              ))}
            </div>
            
            <button
              onClick={handleNext}
              disabled={step.action && !actionCompleted}
              className={`flex items-center gap-1 px-3 py-2 sm:px-6 sm:py-3 rounded-xl font-bold transition-all text-xs sm:text-base ${
                isLastStep
                  ? 'bg-gradient-to-r from-mint-green to-sky-blue text-white hover:shadow-lg'
                  : step.action && !actionCompleted
                    ? 'bg-soft-gray text-text-medium cursor-not-allowed'
                    : 'bg-gradient-to-r from-sunny-yellow to-coral-orange text-white hover:shadow-lg'
              }`}
            >
              {isLastStep ? "Let's Go!" : 'Next'}
              {!isLastStep && <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />}
              {isLastStep && <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
          
          {/* Skip tour link */}
          <div className="pb-3 sm:pb-4 text-center shrink-0">
            <button 
              onClick={handleSkip}
              className="text-text-medium hover:text-coral-orange text-xs sm:text-sm underline transition-colors"
            >
              Skip tour and start playing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
